import React, { useState, useRef, useEffect, useCallback } from "react";
import _ from "lodash";
import "./assets/timer.css";
import sound from "./assets/alarm-clock.mp3";
import image from "./assets/WORKFREAK_LOGO.png";
import arrow from "./assets/scroll-down-arrow.png";
import { formatTimeDisplay } from "./utils/helpers";

const Timer = ({ onAddTimerSession, onMarkTaskCompleted }) => {
  // Core timer state - minimal and focused
  const [time, setTime] = useState(0);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const [linkedTask, setLinkedTask] = useState(null);

  // Drag interaction state
  const [dragging, setDragging] = useState(false);
  const [lastYPosition, setLastYPosition] = useState(0);
  const [dragUpdatePaused, setDragUpdatePaused] = useState(false);

  // Refs for stable values that don't need re-renders
  const animationFrameRef = useRef(null);
  const interactiveRef = useRef(null);
  const lastUpdateTime = useRef(Date.now());
  const endSound = useRef(new Audio(sound));
  const cumulativeMovement = useRef(0);
  const timerStartTime = useRef(null);
  const originalTimeRef = useRef(0);
  const sessionSavedRef = useRef(false); // Prevent double saving
  const effectRunningRef = useRef(false); // Prevent multiple effect runs
  const timerStartTimestamp = useRef(null); // Track actual start time for accurate timing

  // Update document title with visual indicator when timer is running
  useEffect(() => {
    if (isCountdownActive && !isPaused) {
      document.title = "● WORK - Productivity Timer";
    } else {
      document.title =
        "Get Productive, FREE Online Digital Timer for Effective Work and More Free Time! Visit Workfreak.com for more details";
    }
  }, [isCountdownActive, isPaused]);

  // Main countdown logic - simplified and stable
  useEffect(() => {
    if (
      isCountdownActive &&
      !isPaused &&
      !animationFrameRef.current &&
      !effectRunningRef.current
    ) {
      effectRunningRef.current = true;
      timerStartTime.current = new Date().toISOString();
      timerStartTimestamp.current = Date.now(); // Track actual start timestamp
      sessionSavedRef.current = false; // Reset session save flag

      const animate = () => {
        if (!isCountdownActive || isPaused) {
          animationFrameRef.current = null;
          effectRunningRef.current = false;
          return;
        }

        const now = Date.now();
        const elapsed = Math.floor((now - timerStartTimestamp.current) / 1000);
        const remainingTime = Math.max(0, originalTimeRef.current - elapsed);

        setTime(remainingTime);

        if (remainingTime <= 0) {
          // Timer completed
          animationFrameRef.current = null;
          effectRunningRef.current = false;

          // Save session only once
          if (!sessionSavedRef.current && onAddTimerSession) {
            sessionSavedRef.current = true;
            const sessionData = {
              taskId: null, // No direct task linking in timer
              duration: originalTimeRef.current,
              startTime: timerStartTime.current,
              endTime: new Date().toISOString(),
            };

            onAddTimerSession(sessionData);
          }

          // Handle timer completion
          setIsFlashing(true);
          setTimeout(() => {
            setIsFlashing(false);
            setIsCountdownActive(false);
          }, 3000);

          // Play sound
          endSound.current.play();
          setTimeout(() => {
            endSound.current.pause();
            endSound.current.currentTime = 0;
          }, 3000);
        } else {
          // Continue animation
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    } else if (!isCountdownActive && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
      effectRunningRef.current = false; // Reset effect running flag
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isCountdownActive, isPaused, onAddTimerSession]);

  // Format time for display
  const formatTime = useCallback(() => {
    return formatTimeDisplay(time);
  }, [time]);

  // Drag interaction handlers
  const handleStart = useCallback(() => {
    setDragging(true);
    if (interactiveRef.current) {
      interactiveRef.current.style.cursor = "grabbing";
    }
    lastUpdateTime.current = Date.now();
  }, []);

  const handleMove = useCallback(
    (positionY) => {
      if (!dragging || isCountdownActive || dragUpdatePaused) return;

      const currentY = positionY;
      cumulativeMovement.current += currentY - lastYPosition;

      const currentTime = Date.now();
      const timeElapsedSinceLastUpdate = currentTime - lastUpdateTime.current;

      const fastDragSpeedThreshold = 10;
      let timeChange =
        Math.abs(cumulativeMovement.current) > fastDragSpeedThreshold ? 60 : 5;
      const isMovingDown = cumulativeMovement.current > 0;

      let newTime = isMovingDown ? time - timeChange : time + timeChange;
      newTime = Math.max(0, Math.min(newTime, 5995));

      // Sticky values logic
      const stickyValues = [
        5 * 60,
        10 * 60,
        15 * 60,
        25 * 60,
        30 * 60,
        45 * 60,
        60 * 60,
      ];
      for (let stickyValue of stickyValues) {
        if (Math.abs(newTime - stickyValue) <= 15) {
          newTime = stickyValue;
          setDragUpdatePaused(true);
          setTimeout(() => setDragUpdatePaused(false), 300);
          break;
        }
      }

      setTime(newTime);
      lastUpdateTime.current = currentTime;
      setLastYPosition(currentY);

      if (
        Math.abs(cumulativeMovement.current) > 10 ||
        timeElapsedSinceLastUpdate > 100
      ) {
        cumulativeMovement.current = 0;
      }
    },
    [dragging, isCountdownActive, dragUpdatePaused, time, lastYPosition]
  );

  const handleEnd = useCallback(() => {
    setDragging(false);
    if (interactiveRef.current) {
      interactiveRef.current.style.cursor = "grab";
    }
  }, []);

  // Touch handlers
  const handleTouchStart = useCallback((event) => {
    if (event.touches.length > 0) {
      setDragging(true);
      if (interactiveRef.current) {
        interactiveRef.current.style.cursor = "grabbing";
      }
      lastUpdateTime.current = Date.now();
    }
  }, []);

  const handleTouchMoveThrottled = _.throttle((event) => {
    if (event.touches.length > 0 && dragging) {
      const positionY = event.touches[0].clientY;
      if (!dragging || isCountdownActive || dragUpdatePaused) return;

      const currentY = positionY;
      cumulativeMovement.current += currentY - lastYPosition;

      const currentTime = Date.now();

      const fastDragSpeedThreshold = 10;
      let timeChange =
        Math.abs(cumulativeMovement.current) > fastDragSpeedThreshold ? 60 : 5;
      const isMovingDown = cumulativeMovement.current > 0;

      let newTime = isMovingDown ? time - timeChange : time + timeChange;
      newTime = Math.max(0, Math.min(newTime, 5995));

      // Sticky values logic
      const stickyValues = [
        5 * 60,
        10 * 60,
        15 * 60,
        25 * 60,
        30 * 60,
        45 * 60,
        60 * 60,
      ];
      for (let stickyValue of stickyValues) {
        if (Math.abs(newTime - stickyValue) <= 15) {
          newTime = stickyValue;
          setDragUpdatePaused(true);
          setTimeout(() => setDragUpdatePaused(false), 300);
          break;
        }
      }

      setTime(newTime);
      setLastYPosition(currentY);
      lastUpdateTime.current = currentTime;
    }
  }, 100);

  const handleTouchMove = handleTouchMoveThrottled;

  const handleTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  // Timer control functions
  const startCountdown = useCallback(() => {
    if (time > 0 && !isCountdownActive) {
      originalTimeRef.current = time;
      setIsCountdownActive(true);
      setIsPaused(false);
    }
  }, [time, isCountdownActive]);

  const pauseTimer = useCallback(() => {
    setIsPaused(true);
    // Store the pause timestamp to adjust for paused time
    if (timerStartTimestamp.current) {
      const now = Date.now();
      const elapsed = Math.floor((now - timerStartTimestamp.current) / 1000);
      timerStartTimestamp.current = now - elapsed * 1000;
    }
  }, []);

  const resumeTimer = useCallback(() => {
    setIsPaused(false);
    // Adjust start timestamp to account for paused time
    if (timerStartTimestamp.current) {
      const now = Date.now();
      const elapsed = Math.floor((now - timerStartTimestamp.current) / 1000);
      timerStartTimestamp.current = now - elapsed * 1000;
    }
  }, []);

  const endTimerEarly = useCallback(() => {
    if (!isCountdownActive) return;

    // Calculate elapsed time
    const elapsedTime = originalTimeRef.current - time;

    // Save session with elapsed time
    if (!sessionSavedRef.current && onAddTimerSession) {
      sessionSavedRef.current = true;
      const sessionData = {
        taskId: null,
        duration: elapsedTime,
        startTime: timerStartTime.current,
        endTime: new Date().toISOString(),
      };

      onAddTimerSession(sessionData);
    }

    // Mark linked task as completed if timer was linked to a task
    if (linkedTask && onMarkTaskCompleted) {
      onMarkTaskCompleted(linkedTask.id);
    }

    // Stop the timer
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    effectRunningRef.current = false; // Reset effect running flag
    setIsCountdownActive(false);
    setIsPaused(false);
    setIsFlashing(true);

    // Reset after flash
    setTimeout(() => {
      setIsFlashing(false);
      setTime(0);
      originalTimeRef.current = 0;
      sessionSavedRef.current = false;
      timerStartTime.current = null;
      setLinkedTask(null);
    }, 2000);
  }, [
    isCountdownActive,
    time,
    onAddTimerSession,
    linkedTask,
    onMarkTaskCompleted,
  ]);

  const resetTimer = useCallback(() => {
    if (endSound.current) {
      endSound.current.pause();
      endSound.current.currentTime = 0;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setIsCountdownActive(false);
    setIsPaused(false);
    setTime(0);
    originalTimeRef.current = 0;
    sessionSavedRef.current = false;
    effectRunningRef.current = false; // Reset effect running flag
    setIsFlashing(false);
    timerStartTime.current = null;
  }, []);

  const setPredefinedTime = useCallback((minutes) => {
    setTime(minutes * 60);
  }, []);

  const flashClass = isFlashing ? "flash-animation" : "";

  return (
    <div className="app-display">
      <a href="https://workfreak.me" target="_blank" rel="noopener noreferrer">
        <img src={image} className="logo" alt="workfreak-logo" />
      </a>

      <div className="predefined-times">
        <button onClick={() => setPredefinedTime(5)}>5 Minutes</button>
        <button onClick={() => setPredefinedTime(10)}>10 Minutes</button>
        <button onClick={() => setPredefinedTime(25)}>25 Minutes</button>
        <button onClick={() => setPredefinedTime(45)}>45 Minutes</button>
        <button onClick={() => setPredefinedTime(60)}>60 Minutes</button>
      </div>

      <div className="timer-container">
        <div className="timer-circle">
          <div className="action-buttons">
            {isCountdownActive ? (
              <>
                <button
                  className="start-button"
                  onClick={isPaused ? resumeTimer : pauseTimer}
                >
                  {isPaused ? "Resume" : "Pause"}
                </button>
                <button className="end-button" onClick={endTimerEarly}>
                  End Now
                </button>
              </>
            ) : (
              <button className="start-button" onClick={startCountdown}>
                Start
              </button>
            )}
            <button className="reset-button" onClick={resetTimer}>
              Reset
            </button>
          </div>

          <div
            className="interactive-area"
            ref={interactiveRef}
            onMouseDown={(e) => handleStart(e.clientY)}
            onMouseMove={(e) => handleMove(e.clientY)}
            onMouseUp={handleEnd}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img src={arrow} alt="arrow" className="scroll-up" />
            <img src={arrow} alt="arrow" className="scroll-down" />
            <div className="instruction-text">
              <div>Timing Area</div>
              <div>Drag up and down</div>
              <small>(slower drag for fine tune)</small>
            </div>
          </div>

          {isCountdownActive && !isPaused && (
            <div className="timer-status-indicator">
              <div className="status-circle active"></div>
              <span className="status-text">WORK</span>
            </div>
          )}
          <span className={`timer-text ${flashClass}`}>{formatTime()}</span>
        </div>
      </div>

      <div className="product-footer">
        <div className="product-link">GET PRODUTIVITY TIMER</div>
        <div className="product-status">(Currently out of stock)</div>
        <div className="company-info">
          WORKFREAK LLC, 30 N Gould St Ste R, Sheridan, WY 82801, USA
        </div>
      </div>
    </div>
  );
};

export default Timer;
